// src/modules/ambassadors/components/GiftItemSelectModal.tsx - Выбор подарочного набора

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  List,
  Radio,
  Space,
  Tag,
  Spin,
  Empty,
  message,
} from 'antd';
import { SearchOutlined, WarningOutlined } from '@ant-design/icons';
import { MenuItem } from '@/types';
import { configApi } from '@/services/api/config';

const { Search } = Input;

interface GiftItemSelectModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (itemId: number) => Promise<void>;
  currentItemId?: number | null;
}

/**
 * Определяет цвет для индикатора остатков
 */
const getStockColor = (stock: number): string => {
  if (stock > 5) return 'green';
  if (stock >= 1 && stock <= 5) return 'orange';
  return 'red';
};

/**
 * Определяет иконку для индикатора остатков
 */
const getStockIcon = (stock: number): React.ReactNode => {
  if (stock > 5) return '✅';
  if (stock >= 1 && stock <= 5) return <WarningOutlined style={{ color: '#faad14' }} />;
  return '❌';
};

export const GiftItemSelectModal: React.FC<GiftItemSelectModalProps> = ({
  visible,
  onClose,
  onSelect,
  currentItemId,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Загрузка списка блюд
  const loadMenuItems = async () => {
    setLoading(true);
    try {
      const response = await configApi.getMenuItems({
        page: 1,
        per_page: 1000, // Загружаем все блюда
        status: 'active', // Только активные
      });

      const items = response.data.data.items;
      setMenuItems(items);
      setFilteredItems(items);
      
      // Устанавливаем текущий выбранный элемент
      if (currentItemId) {
        setSelectedItemId(currentItemId);
      }
    } catch (error) {
      message.error('Не удалось загрузить список блюд');
      console.error('Error loading menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка при открытии модального окна
  useEffect(() => {
    if (visible) {
      loadMenuItems();
      setSearchQuery('');
    }
  }, [visible]);

  // Фильтрация по поисковому запросу
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(menuItems);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = menuItems.filter(item =>
      item.name.toLowerCase().includes(query)
    );
    setFilteredItems(filtered);
  }, [searchQuery, menuItems]);

  // Обработка выбора элемента
  const handleSelect = async () => {
    if (!selectedItemId) {
      message.warning('Выберите блюдо');
      return;
    }

    setSubmitting(true);
    try {
      await onSelect(selectedItemId);
      message.success('Подарочный набор успешно обновлён');
      onClose();
    } catch (error) {
      message.error('Не удалось обновить подарочный набор');
      console.error('Error updating gift item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Проверка, можно ли выбрать блюдо (остаток > 0)
  const isItemDisabled = (item: MenuItem): boolean => {
    return (item.stock_available || 0) === 0;
  };

  return (
    <Modal
      title="Выбрать подарочный набор"
      open={visible}
      onCancel={onClose}
      onOk={handleSelect}
      confirmLoading={submitting}
      width={600}
      okText="Сохранить"
      cancelText="Отмена"
    >
      {/* Поиск */}
      <Search
        placeholder="Поиск блюда по названию..."
        prefix={<SearchOutlined />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ marginBottom: 16 }}
        allowClear
      />

      {/* Список блюд */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Empty description="Блюда не найдены" />
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <Radio.Group
            value={selectedItemId}
            onChange={(e) => setSelectedItemId(e.target.value)}
            style={{ width: '100%' }}
          >
            <List
              dataSource={filteredItems}
              renderItem={(item) => {
                const disabled = isItemDisabled(item);
                const stockAvailable = item.stock_available || 0;
                const stockColor = getStockColor(stockAvailable);
                const stockIcon = getStockIcon(stockAvailable);

                return (
                  <List.Item
                    style={{
                      padding: '12px 16px',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      background: selectedItemId === item.id ? '#f0f5ff' : 'transparent',
                      borderRadius: 4,
                    }}
                    onClick={() => !disabled && setSelectedItemId(item.id)}
                  >
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: '100%' }}
                    >
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                          <Radio value={item.id} disabled={disabled} />
                          <span style={{ fontWeight: 500, fontSize: 14 }}>
                            {item.name}
                          </span>
                        </Space>
                        <Tag color={stockColor}>
                          {stockIcon} {stockAvailable} шт.
                        </Tag>
                      </Space>
                      
                      <div style={{ 
                        marginLeft: 24, 
                        fontSize: 12, 
                        color: '#8c8c8c' 
                      }}>
                        Цена: {item.price.toLocaleString('ru-RU')} ₸
                      </div>

                      {/* Предупреждение при низком остатке */}
                      {!disabled && stockAvailable >= 1 && stockAvailable <= 5 && (
                        <div style={{
                          marginLeft: 24,
                          padding: '4px 8px',
                          background: '#fffbe6',
                          border: '1px solid #ffe58f',
                          borderRadius: 4,
                          fontSize: 11,
                          color: '#ad6800'
                        }}>
                          ⚠️ Низкий остаток! Рекомендуем пополнить запасы
                        </div>
                      )}

                      {/* Сообщение о недоступности */}
                      {disabled && (
                        <div style={{
                          marginLeft: 24,
                          padding: '4px 8px',
                          background: '#fff1f0',
                          border: '1px solid #ffa39e',
                          borderRadius: 4,
                          fontSize: 11,
                          color: '#cf1322'
                        }}>
                          ❌ Товар закончился на складе
                        </div>
                      )}
                    </Space>
                  </List.Item>
                );
              }}
            />
          </Radio.Group>
        </div>
      )}
    </Modal>
  );
};